import React, { useState, useEffect } from 'react';
import { ConversationSummary, ConversationDetail, fetchExotelConversations, fetchExotelConversation, reanalyzeExotelConversation } from '../utils/api';
import './ReportsHistory.css';

interface ReportsHistoryProps {
  userRole: 'super_admin' | 'admin' | 'customer';
}

const ReportsHistory: React.FC<ReportsHistoryProps> = ({ userRole }) => {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number | string>(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filters state
  const [searchPhone, setSearchPhone] = useState('');
  const [searchCampaign, setSearchCampaign] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [selectedSid, setSelectedSid] = useState<string | null>(null);
  const [selectedConv, setSelectedConv] = useState<ConversationDetail | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [reanalyzingSid, setReanalyzingSid] = useState<string | null>(null);
  const [reanalyzeError, setReanalyzeError] = useState('');
  const [expandedJsonSids, setExpandedJsonSids] = useState<Record<string, boolean>>({});
  const [playingSid, setPlayingSid] = useState<string | null>(null);
  const [audioInstance, setAudioInstance] = useState<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      if (audioInstance) {
        audioInstance.pause();
      }
    };
  }, [audioInstance]);

  const togglePlay = (callSid: string, url: string) => {
    if (playingSid === callSid && audioInstance) {
      audioInstance.pause();
      setPlayingSid(null);
    } else {
      if (audioInstance) {
        audioInstance.pause();
      }
      const newAudio = new Audio(url);
      newAudio.play().catch(err => {
        console.error("Playback failed:", err);
      });
      newAudio.onended = () => {
        setPlayingSid(null);
      };
      setAudioInstance(newAudio);
      setPlayingSid(callSid);
    }
  };

  const toggleJsonRow = (sid: string) => {
    setExpandedJsonSids((prev) => ({
      ...prev,
      [sid]: !prev[sid],
    }));
  };

  // Load conversations list
  const loadConversationsList = async (page: number = 1, size: number | string = 10, phoneVal?: string, campaignVal?: string, fromVal?: string, toVal?: string) => {
    setLoading(true);
    try {
      const res = await fetchExotelConversations(
        page,
        size,
        phoneVal !== undefined ? phoneVal : searchPhone,
        campaignVal !== undefined ? campaignVal : searchCampaign,
        fromVal !== undefined ? fromVal : fromDate,
        toVal !== undefined ? toVal : toDate
      );
      
      if (res && 'data' in res) {
        setConversations(res.data);
        setTotalRecords(res.total);
        setCurrentPage(res.page);
        setPageSize(res.limit);
      } else {
        // Fallback for backward compatibility
        const list = res || [];
        setConversations(list.sort((a: any, b: any) => b.startTime.localeCompare(a.startTime)));
        setTotalRecords(list.length);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConversationsList(1, pageSize);
  }, []);

  // Fetch individual conversation details when modal is triggered
  const handleOpenTranscript = async (callSid: string) => {
    setSelectedSid(callSid);
    setModalLoading(true);
    try {
      const detail = await fetchExotelConversation(callSid);
      setSelectedConv(detail);
    } catch (err) {
      console.error('Failed to load conversation details:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedSid(null);
    setSelectedConv(null);
    setReanalyzeError('');
  };

  const handleReanalyze = async (callSid: string) => {
    setReanalyzingSid(callSid);
    setReanalyzeError('');
    try {
      const updated = await reanalyzeExotelConversation(callSid);
      if (selectedSid === callSid) {
        setSelectedConv(updated);
      }
      await loadConversationsList(currentPage, pageSize);
    } catch (err: any) {
      console.error('Failed to re-analyze conversation:', err);
      setReanalyzeError(err.message || 'Failed to re-analyze with Ollama');
    } finally {
      setReanalyzingSid(null);
    }
  };

  // The server handles filtering dynamically, so conversations represents the current page of filtered logs.
  const filteredConversations = conversations;

  const clearFilters = () => {
    setSearchPhone('');
    setSearchCampaign('');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
    loadConversationsList(1, pageSize, '', '', '', '');
  };

  const downloadReportsCSV = async () => {
    setLoading(true);
    try {
      // Fetch all matching records without pagination limit for full export
      const res = await fetchExotelConversations(
        undefined,
        undefined,
        searchPhone,
        searchCampaign,
        fromDate,
        toDate
      );
      
      const allRecords = Array.isArray(res) ? res : (res?.data || []);
      if (allRecords.length === 0) return;
      
      const headers = [
        'Call SID',
        'From Number',
        'Customer Name',
        'Start Time',
        'Campaign Name',
        'Script Used',
        'Duration (seconds)',
        'AI Summary',
        'AI Intent',
        'AI Status Suggestion',
        'Follow-up Time',
        'PTP Date',
        'Payment Link Required',
        'Risk Category'
      ];
      const rows = allRecords.map((c: ConversationSummary) => {
        let intent = 'N/A';
        if (c.aiStatusJson) {
          try {
            const parsed = JSON.parse(c.aiStatusJson);
            intent = parsed.intent || 'N/A';
          } catch {}
        }

        return [
          c.callSid,
          c.from,
          c.customerName || 'N/A',
          new Date(c.startTime).toLocaleString(),
          c.campaignName || 'N/A',
          c.scriptName,
          c.durationSeconds || '0',
          (c.summary || '').replace(/"/g, '""'),
          intent.replace(/"/g, '""'),
          c.aiStatusSuggestion || 'Not Interested',
          c.followupDateTime || 'N/A',
          c.ptpDate || 'N/A',
          c.sendPaymentLink ? 'Yes' : 'No',
          c.riskCategory || 'low'
        ];
      });

      const csvContent =
        'data:text/csv;charset=utf-8,' +
        [headers.join(','), ...rows.map((e: any[]) => e.map((val: any) => `"${val}"`).join(','))].join('\n');

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `Voice_AI_Call_Report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export CSV:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reports-section">
      
      {/* ── Filter Bar ── */}
      <div className="filters-card">
        <h4 className="filters-title">🔍 Search & Filter Call Logs</h4>
        
        <div className="filters-grid">
          <div className="filter-group">
            <label>Customer Mobile Number</label>
            <input
              type="text"
              placeholder="e.g. 08600432414"
              value={searchPhone}
              onChange={(e) => setSearchPhone(e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>Campaign Name</label>
            <input
              type="text"
              placeholder="e.g. BNPL Collections"
              value={searchCampaign}
              onChange={(e) => setSearchCampaign(e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-actions-group">
            <button 
              className="filter-btn search-btn" 
              style={{ background: '#3b82f6', color: '#ffffff', border: 'none' }}
              onClick={() => {
                setCurrentPage(1);
                loadConversationsList(1, pageSize);
              }}
            >
              🔍 Search
            </button>
            <button className="filter-btn clear-btn" onClick={clearFilters}>
              Clear Filters
            </button>
            <button className="filter-btn export-btn" onClick={downloadReportsCSV} disabled={filteredConversations.length === 0}>
              📥 Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* ── Tabular View ── */}
      <div className="table-card">
        {loading ? (
          <div className="table-loader">Loading call records...</div>
        ) : filteredConversations.length === 0 ? (
          <div className="table-empty-state">
            <h4>No Call Records Found</h4>
            <p>Try resetting filters or initiating new calls to log records.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Customer Phone</th>
                  <th>Customer Name</th>
                  <th>Campaign Name</th>
                  <th>Script Assigned</th>
                  <th>Call Time</th>
                  <th>Duration</th>
                  <th>Recording</th>
                  <th>AI Summary</th>
                  <th>Follow-up Time</th>
                  <th>PTP Date</th>
                  <th>Payment Link</th>
                  <th>Risk Level</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'center' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredConversations.map((c) => (
                  <React.Fragment key={c.callSid}>
                    <tr className="table-row">
                      <td>
                        <button 
                          className="clickable-phone-btn"
                          onClick={() => handleOpenTranscript(c.callSid)}
                          title="Click to view transcript"
                        >
                          📞 {c.from}
                        </button>
                      </td>
                      <td className="font-semibold text-slate-700">{c.customerName || 'N/A'}</td>
                      <td>
                        <span className={`campaign-tag-badge ${c.campaignName ? 'active' : 'direct'}`}>
                          {c.campaignName || 'Direct Outbound'}
                        </span>
                      </td>
                      <td className="font-semibold text-slate-700">{c.scriptName}</td>
                      <td className="text-slate-500 font-medium">
                        {new Date(c.startTime).toLocaleString()}
                      </td>
                      <td className="font-semibold text-indigo-600">
                        {c.durationSeconds ? `${c.durationSeconds}s` : '—'}
                      </td>
                      <td>
                        {c.recordingUrl ? (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <button 
                              onClick={() => togglePlay(c.callSid, c.recordingUrl!)} 
                              style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                width: '32px', 
                                height: '32px', 
                                borderRadius: '50%', 
                                border: 'none', 
                                background: playingSid === c.callSid ? '#ef4444' : '#3b82f6', 
                                color: '#ffffff', 
                                cursor: 'pointer',
                                fontSize: '14px',
                                lineHeight: 1,
                                transition: 'all 0.2s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                              }}
                              title={playingSid === c.callSid ? 'Pause recording' : 'Play recording'}
                            >
                              {playingSid === c.callSid ? '⏸' : '▶'}
                            </button>
                            <a 
                              href={c.recordingUrl} 
                              download={`recording-${c.callSid}.mp3`}
                              style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                width: '32px', 
                                height: '32px', 
                                borderRadius: '50%', 
                                border: '1px solid #cbd5e1', 
                                background: '#ffffff', 
                                color: '#475569', 
                                cursor: 'pointer',
                                fontSize: '14px',
                                textDecoration: 'none',
                                lineHeight: 1,
                                transition: 'all 0.2s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                              }}
                              onMouseOver={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#1e293b'; }}
                              onMouseOut={(e) => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.color = '#475569'; }}
                              title="Download recording"
                            >
                              📥
                            </a>
                          </div>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '11px', fontStyle: 'italic' }}>No recording</span>
                        )}
                      </td>
                      <td className="summary-cell-text" title={c.summary || 'Summary not available'}>
                        {c.summary || <span className="text-slate-400">Not summarized</span>}
                      </td>
                      <td>
                        {c.followupDateTime ? (
                          <span className="followup-time-badge" style={{ fontSize: '11px', fontWeight: 600, color: '#92400e', background: '#fef3c7', padding: '3px 8px', borderRadius: '12px' }}>
                            📅 {c.followupDateTime}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td>
                        {(() => {
                          const ptp = c.ptpDate;
                          return ptp ? (
                            <span className="ptp-time-badge" style={{ fontSize: '11px', fontWeight: 600, color: '#0369a1', background: '#e0f2fe', padding: '3px 8px', borderRadius: '12px' }}>
                              📅 {ptp}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          );
                        })()}
                      </td>
                      <td>
                        {c.sendPaymentLink ? (
                          <span style={{ color: '#047857', fontWeight: 700 }}>💳 Yes</span>
                        ) : (
                          <span className="text-slate-400">No</span>
                        )}
                      </td>
                      <td>
                        {(() => {
                          const risk = c.riskCategory || 'low';
                          const riskColor = risk === 'high' ? '#be123c' : risk === 'medium' ? '#b45309' : '#047857';
                          return (
                            <span style={{ color: riskColor, fontWeight: 700, textTransform: 'uppercase', fontSize: '12px' }}>
                              {risk}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        {c.aiStatusJson ? (
                          (() => {
                            const suggestion = c.aiStatusSuggestion || 'Not Interested';
                            const cleanClass = suggestion.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '');
                            return (
                              <div className="status-cell" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span className={`status-badge ${cleanClass}`}>
                                  {suggestion}
                                </span>
                                <button 
                                  className="view-json-toggle-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleJsonRow(c.callSid);
                                  }}
                                  title="View AI Status JSON"
                                >
                                  {expandedJsonSids[c.callSid] ? 'Hide' : 'JSON'}
                                </button>
                              </div>
                            );
                          })()
                        ) : (
                          <span className="text-slate-400 font-medium">Pending</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button 
                          className="view-details-action-btn"
                          onClick={() => handleOpenTranscript(c.callSid)}
                        >
                          View Transcript
                        </button>
                      </td>
                    </tr>
                    {expandedJsonSids[c.callSid] && (
                      <tr className="expanded-json-row">
                        <td colSpan={12} className="expanded-json-cell">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>📋 Ollama 2B Status JSON</span>
                            <button
                              className="reanalyze-btn"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReanalyze(c.callSid);
                              }}
                              disabled={reanalyzingSid === c.callSid}
                            >
                              {reanalyzingSid === c.callSid ? '🔄 Re-analyzing...' : '🔄 Re-analyze'}
                            </button>
                          </div>
                          {c.aiStatusJson ? (
                            <pre className="expanded-json-box">
                              {JSON.stringify(JSON.parse(c.aiStatusJson), null, 2)}
                            </pre>
                          ) : (
                            <p className="no-status-text">No status JSON available.</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && totalRecords > 0 && (
          <div className="pagination-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px', padding: '10px 15px', background: '#ffffff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                Showing <strong>{currentPage === 1 ? 1 : (currentPage - 1) * (typeof pageSize === 'number' ? pageSize : totalRecords) + 1}</strong> to <strong>{Math.min(currentPage * (typeof pageSize === 'number' ? pageSize : totalRecords), totalRecords)}</strong> of <strong>{totalRecords}</strong> records
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Show:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    const val = e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10);
                    setPageSize(val);
                    setCurrentPage(1);
                    loadConversationsList(1, val);
                  }}
                  style={{ padding: '4px 8px', fontSize: '13px', borderRadius: '4px', border: '1px solid #cbd5e1', outline: 'none', background: '#ffffff', cursor: 'pointer' }}
                >
                  <option value={10}>10</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={400}>400</option>
                  <option value={800}>800</option>
                  <option value="all">All</option>
                </select>
              </div>
            </div>
            {pageSize !== 'all' && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => {
                    if (currentPage > 1) {
                      const nextP = currentPage - 1;
                      setCurrentPage(nextP);
                      loadConversationsList(nextP, pageSize);
                    }
                  }}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                  style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '4px', border: '1px solid #cbd5e1', background: currentPage === 1 ? '#f1f5f9' : '#ffffff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                >
                  ◀ Previous
                </button>
                <button
                  onClick={() => {
                    if (currentPage * (pageSize as number) < totalRecords) {
                      const nextP = currentPage + 1;
                      setCurrentPage(nextP);
                      loadConversationsList(nextP, pageSize);
                    }
                  }}
                  disabled={currentPage * (pageSize as number) >= totalRecords}
                  className="pagination-btn"
                  style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '4px', border: '1px solid #cbd5e1', background: currentPage * (pageSize as number) >= totalRecords ? '#f1f5f9' : '#ffffff', cursor: currentPage * (pageSize as number) >= totalRecords ? 'not-allowed' : 'pointer' }}
                >
                  Next ▶
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Detailed Transcript Modal ── */}
      {selectedSid && (
        <div className="modal-backdrop-overlay" onClick={handleCloseModal}>
          <div className="modal-container-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>Call Details: {selectedConv?.from || selectedSid}</h3>
              <button className="modal-close-btn" onClick={handleCloseModal}>&times;</button>
            </div>
            
            {modalLoading ? (
              <div className="modal-body-loader">Fetching call transcript and recording data...</div>
            ) : selectedConv ? (
              <div className="modal-body-scroller">
                
                {/* Audio Player Banner */}
                {selectedConv.recordingUrl && (
                  <div className="modal-recording-player" style={{ marginBottom: '15px', padding: '12px', background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.15)', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#1e3a8a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      🔊 Call Recording Audio
                    </h4>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                      <audio src={selectedConv.recordingUrl} controls style={{ flex: 1, height: '36px' }} />
                      <a 
                        href={selectedConv.recordingUrl} 
                        download={`recording-${selectedConv.callSid}.mp3`}
                        style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          gap: '6px', 
                          padding: '8px 16px', 
                          background: '#2563eb', 
                          color: '#ffffff', 
                          border: 'none', 
                          borderRadius: '6px', 
                          fontSize: '12px', 
                          fontWeight: 600, 
                          textDecoration: 'none',
                          cursor: 'pointer',
                          transition: 'background 0.2s' 
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#1d4ed8'}
                        onMouseOut={(e) => e.currentTarget.style.background = '#2563eb'}
                        title="Download recording file"
                      >
                        📥 Download MP3
                      </a>
                    </div>
                  </div>
                )}

                {/* AI Summary Banner */}
                <div className="modal-summary-box">
                  <h4>🧠 AI Summary (English Characters Only)</h4>
                  <p>{selectedConv.summary || 'AI Summary generating or not available for this call.'}</p>
                </div>

                {/* AI Status Suggestions (Ollama 2B) */}
                <div className="modal-status-box">
                  <div className="modal-status-header">
                    <h4>📋 AI Status & Intent (Local Ollama 2B)</h4>
                    <button
                      className="reanalyze-btn"
                      onClick={() => handleReanalyze(selectedConv.callSid)}
                      disabled={reanalyzingSid === selectedConv.callSid}
                      title="Re-run Ollama 2B transcript analysis"
                    >
                      {reanalyzingSid === selectedConv.callSid ? '🔄 Re-analyzing...' : '🔄 Re-analyze with Ollama'}
                    </button>
                  </div>
                  {reanalyzeError && (
                    <div className="reanalyze-error">
                      ⚠️ {reanalyzeError}
                    </div>
                  )}
                  {selectedConv.aiStatusJson ? (
                    <div className="status-json-container">
                      <div className="status-badge-row">
                        <strong>Suggested Status:</strong> 
                        {(() => {
                          let suggestion = 'Not Interested';
                          try {
                            suggestion = JSON.parse(selectedConv.aiStatusJson).ai_status_suggestion || 'Not Interested';
                          } catch {}
                          const cleanClass = suggestion.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '');
                          return (
                            <span className={`status-badge ${cleanClass}`}>
                              {suggestion}
                            </span>
                          );
                        })()}
                      </div>
                      <pre className="status-json-pre">
                        {JSON.stringify(JSON.parse(selectedConv.aiStatusJson), null, 2)}
                      </pre>
                    </div>
                  ) : (
                    <p className="no-status-text">Ollama status analysis is pending or not available for this call.</p>
                  )}
                </div>

                {/* Call Metadata Grid */}
                <div className="modal-meta-grid">
                  <div className="meta-item">
                    <span>Call SID</span>
                    <strong>{selectedConv.callSid}</strong>
                  </div>
                  <div className="meta-item">
                    <span>Script template</span>
                    <strong>{selectedConv.scriptName}</strong>
                  </div>
                  <div className="meta-item">
                    <span>Duration</span>
                    <strong>{selectedConv.durationSeconds ? `${selectedConv.durationSeconds} seconds` : '—'}</strong>
                  </div>
                  <div className="meta-item">
                    <span>Start time</span>
                    <strong>{new Date(selectedConv.startTime).toLocaleString()}</strong>
                  </div>
                  {selectedConv.followupDateTime && (
                    <div className="meta-item">
                      <span>Follow-up callback</span>
                      <strong style={{ color: '#b45309' }}>📅 {selectedConv.followupDateTime}</strong>
                    </div>
                  )}
                  {(() => {
                    try {
                      if (selectedConv.aiStatusJson) {
                        const parsed = JSON.parse(selectedConv.aiStatusJson);
                        const ptp = parsed.PTP_date || parsed.ptp_date;
                        if (ptp) {
                          return (
                            <div className="meta-item">
                              <span>Promise to Pay (PTP)</span>
                              <strong style={{ color: '#0369a1' }}>📅 {ptp}</strong>
                            </div>
                          );
                        }
                      }
                    } catch {}
                    return null;
                  })()}
                  {(() => {
                    try {
                      if (selectedConv.aiStatusJson) {
                        const parsed = JSON.parse(selectedConv.aiStatusJson);
                        const risk = parsed.risk_category || 'low';
                        const riskColor = risk === 'high' ? '#be123c' : risk === 'medium' ? '#b45309' : '#047857';
                        return (
                          <div className="meta-item">
                            <span>Risk Category</span>
                            <strong style={{ color: riskColor, textTransform: 'uppercase' }}>⚠️ {risk}</strong>
                          </div>
                        );
                      }
                    } catch {}
                    return null;
                  })()}
                  {(() => {
                    try {
                      if (selectedConv.aiStatusJson && JSON.parse(selectedConv.aiStatusJson).send_payment_link) {
                        return (
                          <div className="meta-item">
                            <span>Payment Link</span>
                            <strong style={{ color: '#047857' }}>💳 Requested</strong>
                          </div>
                        );
                      }
                    } catch {}
                    return null;
                  })()}
                </div>

                {/* Chat Transcript Turns */}
                <h4 className="transcript-section-title">💬 Conversation Transcript (English Translation)</h4>
                
                <div className="modal-chat-transcript">
                  {selectedConv.turns.length === 0 ? (
                    <div className="transcript-empty-state">No transcript turns recorded for this conversation.</div>
                  ) : (
                    selectedConv.turns.map((turn, i) => (
                      <div key={i} className={`chat-turn-bubble ${turn.role}`}>
                        <span className="turn-role-label">
                          {turn.role === 'agent' ? '🤖 Agent' : '👤 Customer'}
                        </span>
                        <span className="turn-timestamp-label">
                          {new Date(turn.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </span>
                        <p className="turn-speech-text">{turn.text}</p>
                      </div>
                    ))
                  )}
                </div>

              </div>
            ) : (
              <div className="modal-body-error">Could not retrieve conversation logs. Please try again.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsHistory;
